import React from 'react';
import '../../css/modal.css';
import {
  BookingActivitySummary,
  BookingComponentsSummary,
  BookingFlightSummary,
  BookingHotelSummary,
  BookingInsuranceSummary,
  BookingTransferSummary,
  BookingVisaSummary,
  formatDisplayDate,
  formatDisplayDateTime,
  hasBookingComponentData,
} from '../../utils/bookingComponents';

type BookingComponentRow =
  | { type: 'hotel'; item: BookingHotelSummary; index: number }
  | { type: 'flight'; item: BookingFlightSummary; index: number }
  | { type: 'transfer'; item: BookingTransferSummary; index: number }
  | { type: 'activity'; item: BookingActivitySummary; index: number }
  | { type: 'visa'; item: BookingVisaSummary; index: number }
  | { type: 'insurance'; item: BookingInsuranceSummary; index: number };

interface BookingComponentTablesProps {
  summary: BookingComponentsSummary | null;
  compact?: boolean;
  emptyText?: string;
  showEmptyState?: boolean;
  renderVoucherActions?: (row: BookingComponentRow) => React.ReactNode;
}

const BookingComponentTables: React.FC<BookingComponentTablesProps> = ({
  summary,
  compact = false,
  emptyText = 'No component details available',
  showEmptyState = true,
  renderVoucherActions,
}) => {
  if (!hasBookingComponentData(summary)) {
    return showEmptyState ? (
      <div className="booking-draft-summary__empty">{emptyText}</div>
    ) : null;
  }

  const tableClassName = compact
    ? 'booking-draft-summary__table booking-draft-summary__table--compact'
    : 'booking-draft-summary__table';
  const hasVoucherActions = typeof renderVoucherActions === 'function';
  const renderHotelDetails = (hotel: BookingHotelSummary) => {
    const details = [
      hotel.room ? `Room: ${hotel.room}` : '',
      hotel.starRating ? `${hotel.starRating} star` : '',
      hotel.hotelRef || hotel.hotelCode ? `Ref: ${hotel.hotelRef || hotel.hotelCode}` : '',
      hotel.address ? hotel.address : '',
      hotel.mealPlan ? `Meal: ${hotel.mealPlan}` : '',
    ].filter(Boolean);

    if (details.length === 0) return null;

    return <div className="rfq-muted">{details.join(' | ')}</div>;
  };

  return (
    <div className={`booking-component-tables${compact ? ' booking-component-tables--compact' : ''}`}>
      {!!summary?.hotels.length && (
        <div className="booking-draft-summary__section">
          <div className="booking-draft-summary__section-title">Hotels</div>
          <div className="booking-draft-summary__table-wrap">
            <table className={tableClassName}>
              <thead>
                <tr>
                  <th>Hotel</th>
                  <th>Room</th>
                  <th>City</th>
                  <th>Check-in</th>
                  <th>Check-out</th>
                  {hasVoucherActions ? <th>Voucher</th> : null}
                </tr>
              </thead>
              <tbody>
                {summary.hotels.map((hotel, index) => (
                  <tr key={`${hotel.name}-${hotel.checkInDate}-${index}`}>
                    <td>
                      <div>{hotel.name}</div>
                      {renderHotelDetails(hotel)}
                    </td>
                    <td>{hotel.room || '—'}</td>
                    <td>{hotel.city || '—'}</td>
                    <td>{formatDisplayDate(hotel.checkInDate)}</td>
                    <td>{formatDisplayDate(hotel.checkOutDate)}</td>
                    {hasVoucherActions ? (
                      <td className="booking-draft-summary__voucher-cell">{renderVoucherActions!({ type: 'hotel', item: hotel, index })}</td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!!summary?.flights.length && (
        <div className="booking-draft-summary__section">
          <div className="booking-draft-summary__section-title">Flights</div>
          <div className="booking-draft-summary__table-wrap">
            <table className={tableClassName}>
              <thead>
                <tr>
                  <th>Airline</th>
                  <th>Flight no.</th>
                  <th>Route</th>
                  <th>Departure</th>
                  <th>Arrival</th>
                  <th>Class</th>
                  <th>Stops</th>
                  {hasVoucherActions ? <th>Voucher</th> : null}
                </tr>
              </thead>
              <tbody>
                {summary.flights.map((flight, index) => (
                  <tr key={`${flight.airline}-${flight.flightNumber}-${flight.departureTime}-${index}`}>
                    <td>{flight.airline || '—'}</td>
                    <td>{flight.flightNumber || '—'}</td>
                    <td>{[flight.from || '—', flight.to || '—'].join(' → ')}</td>
                    <td>{formatDisplayDateTime(flight.departureTime)}</td>
                    <td>{formatDisplayDateTime(flight.arrivalTime)}</td>
                    <td>{flight.travelClass || '—'}</td>
                    <td>{flight.stops}</td>
                    {hasVoucherActions ? (
                      <td className="booking-draft-summary__voucher-cell">{renderVoucherActions!({ type: 'flight', item: flight, index })}</td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!!summary?.transfers.length && (
        <div className="booking-draft-summary__section">
          <div className="booking-draft-summary__section-title">Cabs</div>
          <div className="booking-draft-summary__table-wrap">
            <table className={tableClassName}>
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Vehicle</th>
                  <th>Route</th>
                  <th>Date</th>
                  {hasVoucherActions ? <th>Voucher</th> : null}
                </tr>
              </thead>
              <tbody>
                {summary.transfers.map((transfer, index) => (
                  <tr key={`${transfer.serviceType}-${transfer.vehicle}-${transfer.transferDate}-${index}`}>
                    <td>{transfer.serviceType || 'Cab'}</td>
                    <td>{transfer.vehicle || '—'}</td>
                    <td>{transfer.route || '—'}</td>
                    <td>{formatDisplayDate(transfer.transferDate)}</td>
                    {hasVoucherActions ? (
                      <td className="booking-draft-summary__voucher-cell">{renderVoucherActions!({ type: 'transfer', item: transfer, index })}</td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!!summary?.activities.length && (
        <div className="booking-draft-summary__section">
          <div className="booking-draft-summary__section-title">Activities</div>
          <div className="booking-draft-summary__table-wrap">
            <table className={tableClassName}>
              <thead>
                <tr>
                  <th>Activity</th>
                  <th>City</th>
                  <th>Date</th>
                  {hasVoucherActions ? <th>Voucher</th> : null}
                </tr>
              </thead>
              <tbody>
                {summary.activities.map((activity, index) => (
                  <tr key={`${activity.title}-${activity.serviceDate}-${index}`}>
                    <td>{activity.title}</td>
                    <td>{activity.city || '—'}</td>
                    <td>{formatDisplayDate(activity.serviceDate)}</td>
                    {hasVoucherActions ? (
                      <td className="booking-draft-summary__voucher-cell">{renderVoucherActions!({ type: 'activity', item: activity, index })}</td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!!summary?.visas.length && (
        <div className="booking-draft-summary__section">
          <div className="booking-draft-summary__section-title">Visas</div>
          <div className="booking-draft-summary__table-wrap">
            <table className={tableClassName}>
              <thead>
                <tr>
                  <th>Visa</th>
                  <th>Country</th>
                  {hasVoucherActions ? <th>Voucher</th> : null}
                </tr>
              </thead>
              <tbody>
                {summary.visas.map((visa, index) => (
                  <tr key={`${visa.title}-${visa.country}-${index}`}>
                    <td>{visa.title}</td>
                    <td>{visa.country || '—'}</td>
                    {hasVoucherActions ? (
                      <td className="booking-draft-summary__voucher-cell">{renderVoucherActions!({ type: 'visa', item: visa, index })}</td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!!summary?.insurance.length && (
        <div className="booking-draft-summary__section">
          <div className="booking-draft-summary__section-title">Insurance</div>
          <div className="booking-draft-summary__table-wrap">
            <table className={tableClassName}>
              <thead>
                <tr>
                  <th>Plan</th>
                  <th>Coverage start</th>
                  <th>Coverage end</th>
                  {hasVoucherActions ? <th>Voucher</th> : null}
                </tr>
              </thead>
              <tbody>
                {summary.insurance.map((item, index) => (
                  <tr key={`${item.title}-${item.coverageStartDate}-${index}`}>
                    <td>{item.title}</td>
                    <td>{formatDisplayDate(item.coverageStartDate)}</td>
                    <td>{formatDisplayDate(item.coverageEndDate)}</td>
                    {hasVoucherActions ? (
                      <td className="booking-draft-summary__voucher-cell">{renderVoucherActions!({ type: 'insurance', item, index })}</td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingComponentTables;
